import * as cornerstone from "cornerstone-core";
import { CornerstoneElement } from "cornerstone-core";
import * as cornerstoneTools from "cornerstone-tools";
import { Event, EventData } from "cornerstone-tools";
import * as cornerstoneMath from "cornerstone-math";
import { Marker } from "../../api/EvalWorkflowApi";
import CornerstoneToolsWrapper from "../../util/CornerstoneToolsWrapper";
import { Point } from "./Point";

const HANDLE_TRIGGER_DISTANCE = 15;
export const MARKER_COLOR = "#f5e706";

const hasChangeMarker = (data: any): boolean => {
  const marker = data.marker;

  if (!marker) return false;

  return marker.x !== data.handles.end.x || marker.y !== data.handles.end.y;
};

const pageRelativePoint = (element: HTMLElement, marker: Point): Point => {
  const elementRelative = cornerstone.pixelToCanvas(element, {
    x: marker.x,
    y: marker.y,
  });

  const rect = element.getBoundingClientRect();

  return {
    x: window.pageXOffset + rect.left + elementRelative.x,
    y: window.pageYOffset + rect.top + elementRelative.y,
  };
};

export type ChangeMarkerHandler = (marker: Marker) => void;
export type OverMarkerHandler = (marker: Marker, isIn: boolean, point?: Point) => void;
export type UpdateMarkersHandler = (markers: Marker[]) => void;
export type AddingMarkerHandler = (positions: Point, pagePosition: Point) => void;

export interface MarkerToolElement extends CornerstoneElement {
  onChangeMarker?: ChangeMarkerHandler;
  onOverMarker?: OverMarkerHandler;
  updateMarkers?: UpdateMarkersHandler;
  onHandling?: () => void;
  onAdding?: AddingMarkerHandler;
}

export default class MarkerMouseTool extends CornerstoneToolsWrapper.BaseAnnotationToolClass<MarkerToolElement> {
  static NAME = "MarkerMouse";

  constructor(props = {}) {
    const defaultProps = {
      name: MarkerMouseTool.NAME,
      supportedInteractionTypes: ["Mouse"],
    };

    super(props, defaultProps);
  }

  createNewMeasurement(eventData: EventData<MarkerToolElement>): any {
    const goodEventData = eventData?.currentPoints?.image;

    if (!goodEventData) {
      return;
    }

    const { x, y } = eventData.currentPoints.image;

    return {
      visible: true,
      active: true,
      color: undefined,
      handles: {
        end: {
          x,
          y,
          highlight: true,
          active: true,
        },
      },
    };
  }

  distanceFromPoint(element: MarkerToolElement, data: any, coords: any): any {
    // no need to implement
  }

  pointNearTool(element: MarkerToolElement, data: any, coords: any, interactionType?: any): boolean {
    const hasEndHandle = data && data.handles && data.handles.end;
    const validParameters = hasEndHandle;

    if (!validParameters || data.visible === false) {
      return false;
    }

    const probeCoords = cornerstone.pixelToCanvas(element, data.handles.end);

    return cornerstoneMath.point.distance(probeCoords, coords) < HANDLE_TRIGGER_DISTANCE;
  }

  renderToolData(evt: cornerstoneTools.Event): void {
    const eventData = evt.detail;
    const { handleRadius } = this.configuration;
    const toolData = cornerstoneTools.getToolState(evt.currentTarget, this.name);
    const { element } = eventData;

    if (!toolData) {
      return;
    }

    // We have tool data for this element - iterate over each one and draw it
    const context = CornerstoneToolsWrapper.getNewContext(eventData.canvasContext.canvas);

    for (let i = 0; i < toolData.data.length; i++) {
      const data = toolData.data[i];

      if (data.visible === false) {
        continue;
      }

      if (element.onAdding && !data.marker?._label) {
        const positions = { x: data.handles.end.x, y: data.handles.end.y };
        element.onAdding(positions, pageRelativePoint(element, positions));
        break;
      }

      CornerstoneToolsWrapper.draw(context, (context) => {
        const color = data.marker ? MARKER_COLOR : cornerstoneTools.toolColors.getColorIfActive(data);

        // Draw the handles
        CornerstoneToolsWrapper.drawHandles(context, eventData, data.handles, {
          handleRadius,
          color,
          lineWidth: 100,
        });

        const { element } = eventData;

        const marker: Marker = {
          ...data.marker,
          x: data.handles.end.x,
          y: data.handles.end.y,
        };

        const markerCanvas = cornerstone.pixelToCanvas(element, data.handles.end);

        if (marker && marker._label) {
          context.beginPath();
          context.fillStyle = color;
          context.textAlign = "center";
          context.font = "12px Arial";
          context.fillText(marker._label, markerCanvas.x, markerCanvas.y - 10);
        }

        // Attach new marker to data
        if (!data.marker && !data.active) {
          if (typeof element.onChangeMarker === "function") {
            data.marker = marker;

            // New marker call back
            console.debug("MarkerTool - marker added", data.marker);
            element.onChangeMarker(data.marker);
          } else {
            console.warn("No handler onChangeMarker");
          }
        }

        // Dispatch on change region
        if (data.invalidated && !data.active && hasChangeMarker(data)) {
          data.invalidated = false;

          data.marker.x = marker.x;
          data.marker.y = marker.y;

          console.debug("MarkerTool - marker updated", data.marker);
          if (typeof element.onChangeMarker === "function") {
            element.onChangeMarker(data.marker);
          }
        }
      });
    }
  }

  mouseMoveCallback(evt: Event) {
    const eventData = evt.detail;

    cornerstoneTools.toolCoordinates.setCoords(eventData);

    // If we have no tool data for this element, do nothing
    const element = eventData.element;
    const toolData = cornerstoneTools.getToolState(evt.currentTarget, MarkerMouseTool.NAME);

    if (!toolData) {
      return;
    }

    // We have tool data, search through all data
    // And see if we can activate a handle
    let imageNeedsUpdate = false;

    for (let i = 0; i < toolData.data.length; i++) {
      // Get the cursor position in canvas coordinates
      const coords = eventData.currentPoints.canvas;

      const data = toolData.data[i];

      if (CornerstoneToolsWrapper.handleActivator(element, data.handles, coords, HANDLE_TRIGGER_DISTANCE) === true) {
        imageNeedsUpdate = true;
      } else {
        if (
          (this.pointNearTool(element, data, coords) && !data.active) ||
          (!this.pointNearTool(element, data, coords) && data.active)
        ) {
          data.active = !data.active;
          imageNeedsUpdate = true;

          // Trigger over on I/O
          if (typeof element.onOverMarker === "function") {
            if (data.marker && data.marker.x && data.marker.y) {
              element.onOverMarker(data.marker, data.active, pageRelativePoint(element, data.marker));
            }
          }
        }
      }
    }

    // Handle activation status changed, redraw the image
    if (imageNeedsUpdate === true) {
      cornerstone.updateImage(element);
    }
  }

  preMouseDownCallback(evt: Event): boolean {
    const eventData = evt.detail;
    let data: any;
    const element = eventData.element;

    const coords = eventData.startPoints.canvas;
    const toolData = cornerstoneTools.getToolState(evt.currentTarget, this.name);

    if (!toolData) {
      return false;
    }

    for (let i = 0; i < toolData.data.length; i++) {
      data = toolData.data[i];

      if (
        this.pointNearTool(element, data, coords) ||
        CornerstoneToolsWrapper.getHandleNearImagePoint(element, data.handles, coords, HANDLE_TRIGGER_DISTANCE)
      ) {
        if (typeof element.onHandling === "function") {
          element.onHandling();
        }
        return false;
      }
    }

    return false;
  }

  updateMarkers(markers: Marker[]) {
    const element = this.element;

    cornerstoneTools.clearToolState(element, this.name);

    for (const marker of markers) {
      cornerstoneTools.addToolState(element, this.name, {
        marker,
        existing: true,
        color: undefined,
        visible: true,
        active: !!marker._hoveredFrom,
        invalidated: true,
        handles: {
          end: {
            x: marker.x,
            y: marker.y,
            highlight: false,
            active: false,
          },
        },
      });
    }

    cornerstone.updateImage(element);
  }

  registerOnChange(func: ChangeMarkerHandler) {
    this.element.onChangeMarker = func;
  }

  registerOnOver(func: OverMarkerHandler) {
    this.element.onOverMarker = func;
  }

  registerOnHandling(func: () => void) {
    this.element.onHandling = func;
  }

  registerOnAdding(func: AddingMarkerHandler) {
    this.element.onAdding = func;
  }
}
